import React, { createContext, useState, useCallback } from "react";
import { Task, TaskProvider } from "../types";
import { QuestsService } from "../services/QuestsService";
import { getTasksGroupDetails } from "../../../Services/Requests";

interface QuestContextType {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  questInfo: QuestInfo | null;
  fetchQuest: (groupId: number) => void;
  // completeTask: (taskId: number) => Promise<void>;
  claimQuestReward: (taskId: number) => Promise<void>;
}

export const QuestContext = createContext<QuestContextType>({
  tasks: [],
  loading: false,
  error: null,
  questInfo: null,
  fetchQuest: () => {},
  // completeTask: async () => {},
  claimQuestReward: async () => {},
});

interface QuestTask {
  id: number;
  name: string;
  slug: string;
  icon?: string;
  link?: string;
  type: string;
  description: string;
  status: "DONE" | "CLAIMED" | "NOT_STARTED" | "IN_PROGRESS";
  target_progress: number;
  user_progress: number;
  partner: any | null;
  rewards: Array<{
    type: string;
    amount: number;
  }>;
}

interface QuestInfo {
  id: number;
  name: string;
  slug: string;
  type: string;
  icon_url: string;
  description?: string;
  banner_url: string;
  completed_tasks: number;
  total_tasks: number;
  rewards: Array<{
    type: string;
    amount: number;
  }>;
}

interface QuestDetails {
  task_group_id: number;
  slug: string;
  type: string;
  name: string;
  description?: string;
  icon_url?: string;
  banner_url?: string;
  tasks: QuestTask[];
  rewards: Array<{
    type: string;
    amount: number;
  }>;
  completed_tasks?: number;
  total_tasks?: number;
}



// Адаптер для преобразования QuestTask в Task
const adaptQuestTaskToTask = (questTask: QuestTask): Task => {
  // Преобразуем массив наград в объект
  const rewardsObj: { [key: string]: number } = {};
  questTask.rewards.forEach((reward) => {
    rewardsObj[reward.type.toLowerCase()] = reward.amount;
  });

  // Если есть DC награда, добавляем ее в dc
  if (rewardsObj["dc"]) {
    rewardsObj.dc = rewardsObj["dc"];
  }

  // Если есть tickets награда, добавляем ее в tickets
  if (rewardsObj["tickets"]) {
    rewardsObj.tickets = rewardsObj["tickets"];
  }

  return {
    id: questTask.id,
    title: questTask.name,
    status: questTask.status,
    rewards: rewardsObj,
    link: questTask.partner?.link || '',
    image: questTask.icon || '',
    centerIcon: questTask.icon || '',
    description: questTask.description,
    buttonText: "Выполнить",
    provider: "QUEST" as TaskProvider,
    icon: questTask.icon || "",
    type: questTask.type,
    progress: {
      current: questTask.user_progress,
      total: questTask.target_progress,
    },
  };
};

export const QuestProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questInfo, setQuestInfo] = useState<QuestInfo | null>(null);

  const fetchQuest = useCallback((groupId: number) => {
    setLoading(true);
    setError(null);

    try {
      // Загружаем информацию о квесте
      getTasksGroupDetails(groupId, (data: QuestDetails) => {
        console.log("Quest details loaded:", data);

        // Преобразуем задания квеста в обычные задания
        const adaptedTasks = data.tasks.map(adaptQuestTaskToTask);

        // Сохраняем информацию о квесте
        setQuestInfo({
          id: data.task_group_id,
          name: data.name,
          slug: data.slug,
          description: data.description,
          banner_url: data.banner_url || "",
          type: data.type,
          icon_url: data.icon_url || "",
          completed_tasks: data.completed_tasks || 0,
          total_tasks: data.total_tasks || data.tasks.length,
          rewards: data.rewards || [],
        });

        setTasks(adaptedTasks);
        setLoading(false);
      });
    } catch (err) {
      setError("Не удалось загрузить задания квеста");
      setLoading(false);
      console.error("Error fetching quest:", err);
    }
  }, []);

  // const completeTask = useCallback(async (taskId: number) => {
  //   try {
  //     // Используем сервис для квестов
  //     await QuestsService.completeTask(taskId);

  //     // Обновляем статус задания в списке
  //     setTasks(prevTasks =>
  //       prevTasks.map(task =>
  //         task.id === taskId
  //           ? { ...task, status: 'DONE' }
  //           : task
  //       )
  //     );

  //     // Обновляем счетчик выполненных заданий в квесте
  //     if (questInfo) {
  //       setQuestInfo(prev => {
  //         if (!prev) return null;
  //         return {
  //           ...prev,
  //           completed_tasks: prev.completed_tasks + 1
  //         };
  //       });
  //     }
  //   } catch (err) {
  //     console.error('Error completing quest task:', err);
  //   }
  // }, [questInfo]);

  const claimQuestReward = useCallback(async (taskId: number) => {
    try {
      // Используем сервис для квестов
      await QuestsService.claimQuestReward(
        questInfo?.id || 0,
        (response) => {
          console.log('Quest reward claimed successfully:', response);
          
          setTasks(prevTasks => 
            prevTasks.map(task => 
              task.id === taskId 
                ? { ...task, status: 'CLAIMED' } 
                : task
            )
          );
        },
        (error) => {
          console.error('Error claiming quest reward:', error);
        }
      );
    } catch (err) {
      console.error('Error claiming quest reward:', err);
    }
  }, [questInfo]);

  return (
    <QuestContext.Provider
      value={{
        tasks,
        loading,
        error,
        questInfo,
        fetchQuest,
        claimQuestReward,
      }}
    >
      {children}
    </QuestContext.Provider>
  );
};
